import { ButtonType } from "@constellation-academy/epic-ui";
import { useFormik } from "formik";
import { Password } from "primereact/password";
import { classNames } from "primereact/utils";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useTranslation } from "@i18n/hooks";
import { cx } from "@styled-system/css";
import { PasswordLoginSchema } from "./password-login.consts";
import { type PasswordLoginProps, type PasswordLoginFormState } from "./password-login.types";
import { useAuthContext } from "../../../hooks/use-auth-context";
import { inputsWrapperClass } from "../../../screens/login/parts/login-container/login-options.styles";
import { Button } from "../../button";
import { DefaultTextFieldComponent } from "../../DefaultTextInput";
import { lastUsedLoginMethodClass } from "../../social-login-button/social-login-button.styles";
import { ValidatedField, type RenderConfig } from "../../ValidatedField";

export const PasswordLogin = ({ onSubmit, isLoading }: PasswordLoginProps) => {
	const { t } = useTranslation("common");
	const { setLastLoginMethod, isLastLoginMethod } = useAuthContext();

	const form = useFormik<PasswordLoginFormState>({
		initialValues: { email: "", password: "" },
		validateOnChange: false,
		validateOnBlur: false,
		validationSchema: toFormikValidationSchema(PasswordLoginSchema),
		onSubmit: async (values) => {
			setLastLoginMethod("email");
			onSubmit(values);
		},
	});

	const PasswordFieldComponent = ({
		fieldName,
		fieldValue,
		updateField,
		isValid,
		disabled,
		placeholder,
	}: RenderConfig<string>) => (
		<Password
			id={fieldName}
			name={fieldName}
			value={fieldValue}
			placeholder={placeholder}
			onChange={(e) => updateField(e.target.value)}
			disabled={disabled}
			className={classNames({ "p-invalid": !isValid })}
			feedback={false}
			toggleMask
		/>
	);

	return (
		<form onSubmit={form.handleSubmit} className={cx(inputsWrapperClass)}>
			<div className={inputsWrapperClass}>
				<div className={cx(isLastLoginMethod("email") && lastUsedLoginMethodClass)}>
					<ValidatedField<PasswordLoginFormState, string>
						formikConfig={form}
						iconClass="pi-envelope"
						name="email"
						placeholder={t("login.email")}
						disabled={isLoading}
						component={DefaultTextFieldComponent}
					/>
				</div>
				<ValidatedField<PasswordLoginFormState, string>
					formikConfig={form}
					iconClass="pi-lock"
					name="password"
					placeholder={t("login.password")}
					disabled={isLoading}
					component={PasswordFieldComponent}
				/>
			</div>
			<Button
				type={ButtonType.Submit}
				label={isLoading ? t("login.signingIn") : t("login.signIn")}
				disabled={!form.values.email || !form.values.password || isLoading}
			/>
		</form>
	);
};
